import BodyText from '@lyra/core/components/BodyText'
import Link from '@lyra/core/components/Link'
import formatTruncatedAddress from '@lyra/core/utils/formatTruncatedAddress'
import { DepositNetwork } from '@lyra/web/constants/chains'
import { PageId } from '@lyra/web/constants/pages'
import { DEFAULT_WITHDRAW_NETWORK_TOKEN, DepositTokenId, TokenId } from '@lyra/web/constants/tokens'
import { TransactionOptions } from '@lyra/web/constants/transactions'
import { HELP_BRIDGE_URL } from '@lyra/web/constants/urls'
import useAuth from '@lyra/web/hooks/useAuth'
import useLyraFundingBalances from '@lyra/web/hooks/useLyraFundingBalances'
import useTransaction from '@lyra/web/hooks/useTransaction'
import useWithdrawQuote from '@lyra/web/hooks/useWithdrawQuote'
import { getActiveWithdrawTokens } from '@lyra/web/utils/bridge'
import { formatDepositNetworkName } from '@lyra/web/utils/chains'
import { getPagePath } from '@lyra/web/utils/pages'
import {
  formatDepositTokenSymbol,
  getDefaultNetworkForToken,
  getDefaultWithdrawTokenForToken,
  getDepositTokensForToken,
  getSupportedNetworksForDepositTokens,
  getTokenForDepositToken,
  sortDepositTokens,
  sortNetworks,
} from '@lyra/web/utils/tokens'
import { formatTokenSymbol } from '@lyra/web/utils/tokens'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isAddress } from 'viem'

import WalletTransactionModal from '../WalletTransactionModal'
import WithdrawModalRows from './WithdrawModalRows'

/**
 * Hierarchy:
 * - DepositNetwork -> informs available withdraw tokens
 * - DepositToken -> determines destination token address
 */

type WithdrawState = {
  token: TokenId
  network: DepositNetwork
  withdrawToken: DepositTokenId
}

type Props = {
  onClose: () => void
  defaultToken?: TokenId
}

export default function WithdrawModal({ onClose, defaultToken: defaultTokenInput }: Props) {
  const { isAuthenticated } = useAuth()
  // Funding balances are non-zero balances in SCW
  const { fundingBalances } = useLyraFundingBalances()

  const {
    // !!SUPER IMPORTANT!!
    // ownerAddress is used in this component as receiver address for withdrawals
    // FAILURE TO SET THE RECEIVER ADDRESS CORRECTLY WILL RESULT IN LOSS OF FUNDS
    ownerAddress: ownerAddressIMPORTANT,
    bridgeFromLyraChain,
  } = useTransaction()

  const initialWithdrawState: WithdrawState = useMemo(() => {
    const _tokenWithBalance = Object.entries(fundingBalances).find(
      ([_, balance]) => balance.balance > 0
    )
    const token = defaultTokenInput
      ? defaultTokenInput
      : _tokenWithBalance
      ? (_tokenWithBalance[0] as TokenId)
      : TokenId.USDC
    const network = getDefaultNetworkForToken(token)
    const withdrawToken = network ? getDefaultWithdrawTokenForToken(network, token) : undefined
    if (!network || !withdrawToken) {
      return {
        ...DEFAULT_WITHDRAW_NETWORK_TOKEN,
      }
    } else {
      return {
        token,
        network,
        withdrawToken,
      }
    }
  }, [defaultTokenInput, fundingBalances])

  const [
    { network: selectedNetwork, withdrawToken: selectedWithdrawToken, token: selectedToken },
    setWithdrawState,
  ] = useState(initialWithdrawState)

  // TODO: @earthtojake support selection of withdraw token for token
  // e.g. allow users to withdraw USDC to USDC.e or USDC on Arbitrum and Optimism
  const [withdrawAmountInput, setWithdrawAmountInput] = useState<bigint>(BigInt(0))

  // Collaterals with balance in subaccount, OR USDC if there are no balances
  const supportedTokens = useMemo(() => {
    const networkTokens = getActiveWithdrawTokens(selectedNetwork)
    const depositTokensWithBalance = Object.entries(fundingBalances).reduce(
      (withdrawTokens, [token, balance]) => {
        const depositTokenIds = getDepositTokensForToken(selectedNetwork, token as TokenId).filter(
          (d) => networkTokens.includes(d)
        )
        if (depositTokenIds && balance.balance > BigInt(0)) {
          return [...withdrawTokens, ...depositTokenIds]
        }
        return withdrawTokens
      },
      [] as DepositTokenId[]
    )
    return depositTokensWithBalance.length
      ? sortDepositTokens(depositTokensWithBalance)
      : [DepositTokenId.USDC]
  }, [fundingBalances, selectedNetwork])

  const supportedNetworks = useMemo(
    () => sortNetworks(getSupportedNetworksForDepositTokens(supportedTokens)),
    [supportedTokens]
  )

  const isSupported = supportedTokens.includes(selectedWithdrawToken)

  const handleChangeNetwork = useCallback(
    (network: DepositNetwork) => {
      if (network === selectedNetwork) {
        return
      }

      const withdrawToken = getDefaultWithdrawTokenForToken(network, selectedToken)
      if (!withdrawToken) {
        console.warn(`No withdraw token for ${selectedToken} on ${network}`)
        return
      }
      // Has balance
      if (supportedTokens.includes(selectedWithdrawToken)) {
        setWithdrawState((state) => ({
          ...state,
          network,
          withdrawToken,
        }))
      } else {
        const withdrawToken = getDefaultWithdrawTokenForToken(network, TokenId.USDC)
        if (!withdrawToken) {
          console.warn(`No withdraw token for ${selectedToken} on ${network}`)
          return
        }
        setWithdrawState((state) => ({
          ...state,
          network,
          withdrawToken,
        }))
      }
    },
    [selectedNetwork, selectedToken, supportedTokens, selectedWithdrawToken]
  )

  const handleChangeToken = useCallback((withdrawToken: DepositTokenId) => {
    const token = getTokenForDepositToken(withdrawToken)
    setWithdrawState((state) => ({
      ...state,
      token,
      withdrawToken,
    }))
  }, [])

  // Note: too many entrypoints for collateral changes, easiest to useEffect
  useEffect(() => {
    // Reset amount
    setWithdrawAmountInput(BigInt(0))
  }, [selectedToken])

  const { data, isLoading, error } = useWithdrawQuote(
    selectedNetwork,
    selectedToken,
    selectedWithdrawToken,
    withdrawAmountInput
  )

  const fee = data?.fee
  const isDstLiquidityAvailable = data?.isSufficientLiquidity
  const isWithdrawLimitAvailable = data?.isSufficientWithdrawLimit

  // !!SUPER IMPORTANT!!
  // ownerAddress is used in this component as receiver address for withdrawals
  // FAILURE TO SET THE RECEIVER ADDRESS CORRECTLY WILL RESULT IN LOSS OF FUNDS
  const receiverAddressIMPORTANT = ownerAddressIMPORTANT

  const withdrawWrapped = useCallback(
    async (options: TransactionOptions) => {
      if (!receiverAddressIMPORTANT) {
        throw new Error('Wallet not ready')
      }
      if (!isAddress(receiverAddressIMPORTANT)) {
        throw new Error('Address is invalid')
      }
      return await bridgeFromLyraChain(
        receiverAddressIMPORTANT,
        selectedNetwork,
        selectedToken,
        selectedWithdrawToken,
        withdrawAmountInput,
        options
      )
    },
    [
      receiverAddressIMPORTANT,
      bridgeFromLyraChain,
      selectedNetwork,
      selectedToken,
      selectedWithdrawToken,
      withdrawAmountInput,
    ]
  )

  if (!isAuthenticated) {
    return null
  }

  const balance = fundingBalances[selectedToken].balanceBn
  const isInsufficientBalance = withdrawAmountInput > balance
  const isInsufficientAmount = data && withdrawAmountInput && data.fee > withdrawAmountInput
  const tokenSymbol = formatTokenSymbol(selectedToken)
  const withdrawTokenSymbol = formatDepositTokenSymbol(selectedWithdrawToken)

  return (
    <WalletTransactionModal
      onClose={onClose}
      desktopWidth={440}
      transaction={withdrawWrapped}
      loggerAction="withdraw"
      reviewTitle="Withdraw"
      reviewContent={
        <WithdrawModalRows
          selectedNetwork={selectedNetwork}
          onChangeNetwork={handleChangeNetwork}
          supportedNetworks={supportedNetworks}
          balances={fundingBalances}
          supportedTokens={supportedTokens}
          onChangeToken={handleChangeToken}
          amountInput={withdrawAmountInput}
          tokenBalance={balance}
          onChangeAmountInput={setWithdrawAmountInput}
          selectedToken={selectedToken}
          selectedWithdrawToken={selectedWithdrawToken}
          fee={fee}
        />
      }
      isDisabled={
        isLoading ||
        !!error ||
        isInsufficientBalance ||
        isInsufficientAmount ||
        !isDstLiquidityAvailable ||
        !isWithdrawLimitAvailable ||
        !withdrawAmountInput ||
        !receiverAddressIMPORTANT ||
        !isSupported
      }
      reviewButtonLabel={
        !!error
          ? 'Something went wrong'
          : isLoading || !withdrawAmountInput
          ? 'Enter Amount'
          : isInsufficientBalance
          ? 'Insufficient Balance'
          : isInsufficientAmount
          ? 'Fees More Than Amount'
          : !receiverAddressIMPORTANT
          ? 'Wallet not connected'
          : !isSupported
          ? `${formatTokenSymbol(selectedToken)} not supported on ${selectedNetwork}`
          : // !!SUPER IMPORTANT!!
            // always show the user the address they are withdrawing to
            `Withdraw to ${formatTruncatedAddress(receiverAddressIMPORTANT)}`
      }
      notice={
        error
          ? {
              message: error.message,
              status: 'error',
            }
          : !isLoading && !isDstLiquidityAvailable
          ? {
              status: 'warning',
              message: (
                <BodyText color="amber">
                  There is not enough liquidity to withdraw to{' '}
                  {formatDepositNetworkName(selectedNetwork)} at the moment. Try another network.{' '}
                  <Link label="Learn more" href={HELP_BRIDGE_URL} target="_blank" />
                </BodyText>
              ),
            }
          : !isLoading && !isWithdrawLimitAvailable
          ? {
              status: 'warning',
              message: (
                <BodyText color="amber">
                  The daily withdraw limit has been reached for {formatTokenSymbol(selectedToken)}{' '}
                  on {formatDepositNetworkName(selectedNetwork)}. Try another network.{' '}
                  <Link label="Learn more" href={HELP_BRIDGE_URL} target="_blank" />
                </BodyText>
              ),
            }
          : tokenSymbol !== withdrawTokenSymbol
          ? {
              status: 'info',
              message: `Your ${tokenSymbol} will be withdrawn as ${withdrawTokenSymbol} on ${formatDepositNetworkName(
                selectedNetwork
              )}.`,
            }
          : undefined
      }
      completeButtonProps={{
        label: 'View Balances',
        path: getPagePath({ page: PageId.Balances }),
      }}
      skipGeoblockCheckDONOTUSE={selectedToken === TokenId.DRV}
    />
  )
}
